import React, { useState, useMemo } from 'react';
import { Table, Tag, Button, message } from 'antd';
import * as XLSX from 'xlsx';
import classNames from 'classnames';
import { importTuroReservations } from '../../api/setting/turo-reservations';

// Column mapping from Turo CSV headers to our TTuroReservation fields
const TURO_RESERVATION_COLUMN_MAP: Record<string, string> = {
    'reservation id': 'ReservationId',
    reservationid: 'ReservationId',
    guest: 'Guest',
    vehicle: 'Vehicle',
    'vehicle name': 'VehicleName',
    vehiclename: 'VehicleName',
    'vehicle id': 'VehicleId',
    vehicleid: 'VehicleId',
    vin: 'VIN',
    'trip start': 'TripStart',
    tripstart: 'TripStart',
    'trip end': 'TripEnd',
    tripend: 'TripEnd',
    'pickup location': 'PickupLocation',
    pickuplocation: 'PickupLocation',
    'return location': 'ReturnLocation',
    returnlocation: 'ReturnLocation',
    'trip status': 'TripStatus',
    tripstatus: 'TripStatus',
    'check-in odometer': 'CheckInOdometer',
    'checkin odometer': 'CheckInOdometer',
    checkinodometer: 'CheckInOdometer',
    'check-out odometer': 'CheckOutOdometer',
    'checkout odometer': 'CheckOutOdometer',
    checkoutodometer: 'CheckOutOdometer',
    'distance traveled': 'DistanceTraveled',
    distancetraveled: 'DistanceTraveled',
    'trip days': 'TripDays',
    tripdays: 'TripDays',
    'trip price': 'TripPrice',
    tripprice: 'TripPrice',
    'boost price': 'BoostPrice',
    boostprice: 'BoostPrice',
    '3-day discount': 'ThreeDayDiscount',
    '3 day discount': 'ThreeDayDiscount',
    '3-daydiscount': 'ThreeDayDiscount',
    '1-week discount': 'OneWeekDiscount',
    '1 week discount': 'OneWeekDiscount',
    '2-week discount': 'TwoWeekDiscount',
    '2 week discount': 'TwoWeekDiscount',
    '3-week discount': 'ThreeWeekDiscount',
    '3 week discount': 'ThreeWeekDiscount',
    '1-month discount': 'OneMonthDiscount',
    '1 month discount': 'OneMonthDiscount',
    '2-month discount': 'TwoMonthDiscount',
    '2 month discount': 'TwoMonthDiscount',
    '3-month discount': 'ThreeMonthDiscount',
    '3 month discount': 'ThreeMonthDiscount',
    'non-refundable discount': 'NonRefundableDiscount',
    'nonrefundable discount': 'NonRefundableDiscount',
    'early bird discount': 'EarlyBirdDiscount',
    'earlybird discount': 'EarlyBirdDiscount',
    'host promotional credit': 'HostPromotionalCredit',
    hostpromotionalcredit: 'HostPromotionalCredit',
    delivery: 'Delivery',
    'excess distance': 'ExcessDistance',
    excessdistance: 'ExcessDistance',
    extras: 'Extras',
    'cancellation fee': 'CancellationFee',
    cancellationfee: 'CancellationFee',
    'additional usage': 'AdditionalUsage',
    additionalusage: 'AdditionalUsage',
    'late fee': 'LateFee',
    latefee: 'LateFee',
    'improper return fee': 'ImproperReturnFee',
    improperreturnfee: 'ImproperReturnFee',
    'airport operations fee': 'AirportOperationsFee',
    airportoperationsfee: 'AirportOperationsFee',
    'airport parking credit': 'AirportParkingCredit',
    airportparkingcredit: 'AirportParkingCredit',
    'tolls & tickets': 'TollsAndTickets',
    'tolls &amp; tickets': 'TollsAndTickets',
    'tolls and tickets': 'TollsAndTickets',
    'tolls&tickets': 'TollsAndTickets',
    'on-trip ev charging': 'OnTripEvCharging',
    'on-trip evcharging': 'OnTripEvCharging',
    'post-trip ev charging': 'PostTripEvCharging',
    'post-trip evcharging': 'PostTripEvCharging',
    smoking: 'Smoking',
    cleaning: 'Cleaning',
    'fines (paid to host)': 'FinesPaidToHost',
    'fines(paidtohost)': 'FinesPaidToHost',
    'fines paid to host': 'FinesPaidToHost',
    'gas reimbursement': 'GasReimbursement',
    gasreimbursement: 'GasReimbursement',
    'gas fee': 'GasFee',
    gasfee: 'GasFee',
    'other fees': 'OtherFees',
    otherfees: 'OtherFees',
    'sales tax': 'SalesTax',
    salestax: 'SalesTax',
    'total earnings': 'TotalEarnings',
    totalearnings: 'TotalEarnings',
};

// Fields that should be parsed as currency/decimal numbers
const CURRENCY_FIELDS = new Set([
    'TripPrice',
    'BoostPrice',
    'ThreeDayDiscount',
    'OneWeekDiscount',
    'TwoWeekDiscount',
    'ThreeWeekDiscount',
    'OneMonthDiscount',
    'TwoMonthDiscount',
    'ThreeMonthDiscount',
    'NonRefundableDiscount',
    'EarlyBirdDiscount',
    'HostPromotionalCredit',
    'Delivery',
    'ExcessDistance',
    'Extras',
    'CancellationFee',
    'AdditionalUsage',
    'LateFee',
    'ImproperReturnFee',
    'AirportOperationsFee',
    'AirportParkingCredit',
    'TollsAndTickets',
    'OnTripEvCharging',
    'PostTripEvCharging',
    'Smoking',
    'Cleaning',
    'FinesPaidToHost',
    'GasReimbursement',
    'GasFee',
    'OtherFees',
    'SalesTax',
    'TotalEarnings',
]);

// Fields that should be parsed as integers
const INTEGER_FIELDS = new Set([
    'ReservationId',
    'VehicleId',
    'CheckInOdometer',
    'CheckOutOdometer',
    'DistanceTraveled',
    'TripDays',
]);

type TuroReservationCSVUploadProps = {
    file: File | null;
    onClose: () => void;
    onImportComplete: () => void;
};

export const TuroReservationCSVUpload: React.FC<
    TuroReservationCSVUploadProps
> = ({ file, onClose, onImportComplete }) => {
    const [data, setData] = useState<any[]>([]);
    const [columns, setColumns] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const mapHeaderName = (header: string): string => {
        // Decode HTML entities (e.g., &amp; -> &)
        const decoded = header.replace(/&amp;/g, '&');
        const normalized = decoded.trim().toLowerCase();
        return TURO_RESERVATION_COLUMN_MAP[normalized] || header;
    };

    const parseFormattedNumber = (
        input: string | number | undefined
    ): number | undefined => {
        if (input === undefined || input === null || input === '')
            return undefined;
        const str = String(input)
            .replace(/,/g, '')
            .replace(/\$/g, '')
            .replace(/\(/g, '-')
            .replace(/\)/g, '');
        const num = parseFloat(str);
        return isNaN(num) ? undefined : num;
    };

    const parseDateField = (
        input: string | number | undefined
    ): string | undefined => {
        if (input === undefined || input === null || input === '')
            return undefined;
        if (typeof input === 'number') {
            // Excel serial date number
            const date = XLSX.SSF.parse_date_code(input);
            if (date) {
                const d = new Date(
                    date.y,
                    date.m - 1,
                    date.d,
                    date.H || 0,
                    date.M || 0,
                    date.S || 0
                );
                return d.toISOString();
            }
            return undefined;
        }
        // Try to parse as a date string like "2025-12-27 07:00 PM"
        const parsed = new Date(input);
        if (!isNaN(parsed.getTime())) {
            return parsed.toISOString();
        }
        return input?.toString();
    };

    const loadCSVFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = e => {
            const text = e.target?.result as string;

            // Use XLSX to parse CSV (handles quoted fields, etc.)
            const workbook = XLSX.read(text, { type: 'string', raw: true });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (jsonData.length > 0) {
                const rawHeaders = jsonData[0] as string[];
                const rows = jsonData.slice(1);

                // Map headers to our field names
                const mappedHeaders = rawHeaders.map(h =>
                    mapHeaderName(String(h))
                );

                // Filter out empty rows
                const validRows = (rows as any[][]).filter(row =>
                    row.some(
                        cell =>
                            cell !== null && cell !== undefined && cell !== ''
                    )
                );

                // Build display columns
                const tableColumns: any[] = [
                    {
                        title: 'No',
                        dataIndex: 'no',
                        key: 'no',
                        width: 50,
                        fixed: 'left' as const,
                    },
                    ...mappedHeaders.map((header: string, index: number) => ({
                        title: rawHeaders[index] || header,
                        dataIndex: header,
                        key: header,
                        ellipsis: true,
                        width:
                            header === 'PickupLocation' ||
                            header === 'ReturnLocation'
                                ? 200
                                : undefined,
                        render: CURRENCY_FIELDS.has(header)
                            ? (val: any) =>
                                  val !== undefined && val !== null
                                      ? `$${Number(val).toFixed(2)}`
                                      : '-'
                            : undefined,
                    })),
                    {
                        title: 'Valid',
                        dataIndex: 'isValidTag',
                        key: 'isValidTag',
                        width: 80,
                    },
                ];

                const tableData = validRows.map((row: any[], index: number) => {
                    const rowData: any = { key: index, no: index + 1 };
                    let errorMsg = '';

                    mappedHeaders.forEach((header: string, i: number) => {
                        const value = row[i];

                        if (header === 'TripStart' || header === 'TripEnd') {
                            rowData[header] = parseDateField(value);
                            // For display, show a formatted date
                            rowData[`_${header}_display`] = value?.toString();
                        } else if (CURRENCY_FIELDS.has(header)) {
                            rowData[header] = parseFormattedNumber(value);
                        } else if (INTEGER_FIELDS.has(header)) {
                            rowData[header] = parseFormattedNumber(value);
                        } else {
                            rowData[header] = value;
                        }
                    });

                    // Validation: at least ReservationId should be present
                    if (!rowData.ReservationId) {
                        errorMsg = 'Missing Reservation ID';
                    }

                    rowData.isValid = !errorMsg;
                    rowData.errorMessage = errorMsg;
                    rowData.isValidTag = errorMsg
                        ? { tag: 'Error', color: 'red' }
                        : { tag: 'OK', color: 'green' };

                    return rowData;
                });

                setColumns(tableColumns);
                setData(tableData);
            }
        };
        reader.readAsText(file);
    };

    React.useEffect(() => {
        if (file) {
            loadCSVFile(file);
        }
    }, [file]);

    const successCount = useMemo(
        () => data.filter(item => item.isValid !== false).length,
        [data]
    );
    const errorCount = useMemo(
        () => data.filter(item => item.isValid === false).length,
        [data]
    );

    const totalEarnings = useMemo(() => {
        return data.reduce((sum, item) => sum + (item.TotalEarnings || 0), 0);
    }, [data]);

    const handleImport = async () => {
        if (data.length === 0) {
            message.error('No data to import');
            return;
        }
        setLoading(true);
        try {
            const records = data.map(item => ({
                reservationId: item.ReservationId || null,
                guest: item.Guest?.toString() || null,
                vehicle: item.Vehicle?.toString() || null,
                vehicleName: item.VehicleName?.toString() || null,
                vehicleId: item.VehicleId || null,
                vin: item.VIN?.toString() || null,
                tripStart: item.TripStart || null,
                tripEnd: item.TripEnd || null,
                pickupLocation: item.PickupLocation?.toString() || null,
                returnLocation: item.ReturnLocation?.toString() || null,
                tripStatus: item.TripStatus?.toString() || null,
                checkInOdometer: item.CheckInOdometer || null,
                checkOutOdometer: item.CheckOutOdometer || null,
                distanceTraveled: item.DistanceTraveled || null,
                tripDays: item.TripDays || null,
                tripPrice: item.TripPrice || null,
                boostPrice: item.BoostPrice || null,
                threeDayDiscount: item.ThreeDayDiscount || null,
                oneWeekDiscount: item.OneWeekDiscount || null,
                twoWeekDiscount: item.TwoWeekDiscount || null,
                threeWeekDiscount: item.ThreeWeekDiscount || null,
                oneMonthDiscount: item.OneMonthDiscount || null,
                twoMonthDiscount: item.TwoMonthDiscount || null,
                threeMonthDiscount: item.ThreeMonthDiscount || null,
                nonRefundableDiscount: item.NonRefundableDiscount || null,
                earlyBirdDiscount: item.EarlyBirdDiscount || null,
                hostPromotionalCredit: item.HostPromotionalCredit || null,
                delivery: item.Delivery || null,
                excessDistance: item.ExcessDistance || null,
                extras: item.Extras || null,
                cancellationFee: item.CancellationFee || null,
                additionalUsage: item.AdditionalUsage || null,
                lateFee: item.LateFee || null,
                improperReturnFee: item.ImproperReturnFee || null,
                airportOperationsFee: item.AirportOperationsFee || null,
                airportParkingCredit: item.AirportParkingCredit || null,
                tollsAndTickets: item.TollsAndTickets || null,
                onTripEvCharging: item.OnTripEvCharging || null,
                postTripEvCharging: item.PostTripEvCharging || null,
                smoking: item.Smoking || null,
                cleaning: item.Cleaning || null,
                finesPaidToHost: item.FinesPaidToHost || null,
                gasReimbursement: item.GasReimbursement || null,
                gasFee: item.GasFee || null,
                otherFees: item.OtherFees || null,
                salesTax: item.SalesTax || null,
                totalEarnings: item.TotalEarnings || null,
            }));

            const result = await importTuroReservations(records);
            const carsAdded = result?.data?.carsAdded ?? 0;
            const carsUpdated = result?.data?.carsUpdated ?? 0;
            const reservationsLinked = result?.data?.reservationsLinked ?? 0;
            let successMsg = `Successfully imported ${records.length} Turo reservation records`;
            if (carsAdded > 0 || carsUpdated > 0) {
                successMsg += `. Vehicles: ${carsAdded} new added, ${carsUpdated} updated, ${reservationsLinked} reservations linked`;
            }
            message.success(successMsg);
            onImportComplete();
            onClose();
        } catch (error: any) {
            const errorMsg =
                error?.response?.data?.detail ||
                error?.message ||
                'Failed to import Turo reservation data';
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-col items-center justify-end w-full mb-4">
                <div className="flex justify-start items-center w-full gap-2">
                    <div className="font-semibold">Total: {data.length}</div>
                    <div>|</div>
                    <div className="font-semibold text-green-500">
                        Valid: {successCount}
                    </div>
                    <div>|</div>
                    <div className="font-semibold text-red-500">
                        Errors: {errorCount}
                    </div>
                    <div>|</div>
                    <div className="font-semibold text-blue-600">
                        Total Earnings: ${totalEarnings.toFixed(2)}
                    </div>
                </div>
            </div>
            <div className="flex w-full">
                <Table
                    className={classNames('white-header w-full')}
                    columns={[
                        ...columns.map(col => {
                            if (col.key === 'isValidTag') {
                                return {
                                    ...col,
                                    render: (tagInfo: any) => (
                                        <Tag color={tagInfo?.color}>
                                            {tagInfo?.tag}
                                        </Tag>
                                    ),
                                };
                            }
                            // Use custom render for date fields to show original format
                            if (
                                col.key === 'TripStart' ||
                                col.key === 'TripEnd'
                            ) {
                                return {
                                    ...col,
                                    render: (_: any, record: any) =>
                                        record[`_${col.key}_display`] ||
                                        record[col.key] ||
                                        '-',
                                };
                            }
                            return col;
                        }),
                    ]}
                    dataSource={data}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                />
            </div>
            <div className="flex justify-end gap-3 mt-4">
                <Button onClick={onClose}>Cancel</Button>
                <Button type="primary" loading={loading} onClick={handleImport}>
                    Save Turo Reservations
                </Button>
            </div>
        </div>
    );
};
